import React, { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Chip,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput
} from '@mui/material';
import { useAdvertiserData, useAccessControl, useCurrentUserData, useAdvertiserSearch } from './hooks';
import AvailableAdvertisersSection from './AvailableAdvertisersSection';

const AdminAdvertiserAccess = forwardRef(
  (
    {
      formData,
      setFormData,
      errors,
      allUsersList,
      selectedOrgs,
      setSelectedOrgs,
      removedOrgs, // Receive removedOrgs from parent
      advertiserMetaDetails,
      currentUserData,
      fetchAdvertisersListForUserBasedOnEmailId,
      fetchAdvertiserMetaDetails,
      onCurrentAccessDataChange,
      handleChange,
      setIsFormModified,
      onRemovedOrgsChange // Callback to pass removed organizations to parent
    },
    ref
  ) => {
    const {
      editableInvitedUserData,
      hasFetchedData,
      isLoading: isAdvertiserLoading,
      error: advertiserError,
      fetchUserAdvertiserDetails,
      addAdvertiser,
      removeAdvertiser
    } = useAdvertiserData(formData.email, onCurrentAccessDataChange, allUsersList);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [currentAccessPlatform, setCurrentAccessPlatform] = useState('DSP');

    const filteredUsers = useMemo(() => {
      if (!userSearchTerm.trim() || !allUsersList || allUsersList.length === 0) {
        return [];
      }

      const searchLower = userSearchTerm.toLowerCase();
      return allUsersList.filter((user) => {
        const email = user.email_id || user.email || '';
        if (!email.includes('@inmobi.com')) {
          return false;
        }

        return email.toLowerCase().includes(searchLower);
      });
    }, [userSearchTerm, allUsersList]);

    const { currentUserAdvertisers, isLoading: isCurrentUserLoading, error: currentUserError } = useCurrentUserData(currentUserData);

    const {
      canModifyAdvertiser,
      canModifyOrganization,
      getAvailableAdvertisers,
      getFilteredAvailableAdvertisers,
      isAdvertiserInCurrentAccess
    } = useAccessControl(currentUserData, currentUserAdvertisers, editableInvitedUserData);
    useEffect(() => {
      if (formData.email && allUsersList && allUsersList.length > 0) {
        const user = allUsersList.find((u) => u.email_id === formData.email);

        if (user) {
          if (user.org_utc && user.org_utc.length > 0) {
            // DON'T set selectedOrgs here - these are existing organizations
            // selectedOrgs should only contain newly selected organizations to add
            // The existing organizations will be shown in "User's Organizations" section
          }
        }
      }
    }, [formData.email, allUsersList]);

    useEffect(() => {
      if (formData.email && allUsersList && allUsersList.length > 0) {
        const user = allUsersList.find((u) => u.email_id === formData.email);

        if (user && user.role) {
          const isAdmin = user.role === 'admin';
          setFormData((prev) => ({
            ...prev,
            isAdmin: isAdmin
          }));
        }
      }
    }, [formData.email, allUsersList, setFormData]);

    useEffect(() => {
      setSelectedOrgs([]);
    }, [formData.email]);

    // Simple solution: Use editableInvitedUserData for Current Access sections
    // This contains both base data from get_user_advertiser_details AND handles dynamic additions
    const currentAccessUtcAdvertisers = editableInvitedUserData?.advertisers_utc || [];
    const currentAccessJstAdvertisers = editableInvitedUserData?.advertisers_non_utc || [];
    const currentAccessCtvAdvertisers = editableInvitedUserData?.advertisers_ctv || [];
    const currentAccessGtvAdvertisers = editableInvitedUserData?.advertisers_gtv || [];

    // Expose methods to parent component
    useImperativeHandle(
      ref,
      () => ({
        getCurrentAdvertiserData: () => {
          return {
            advertisers_utc: currentAccessUtcAdvertisers,
            advertisers_non_utc: currentAccessJstAdvertisers,
            advertisers_ctv: currentAccessCtvAdvertisers,
            advertisers_gtv: currentAccessGtvAdvertisers
          };
        }
      }),
      [currentAccessUtcAdvertisers, currentAccessJstAdvertisers, currentAccessCtvAdvertisers, currentAccessGtvAdvertisers]
    );

    const {
      searchTerm: currentAccessSearchTerm,
      filteredData: filteredUtcAdvertisers,
      updateSearchTerm: setCurrentAccessSearchTerm
    } = useAdvertiserSearch(currentAccessUtcAdvertisers);

    const {
      searchTerm: jstSearchTerm,
      filteredData: filteredJstAdvertisers,
      updateSearchTerm: setJstSearchTerm
    } = useAdvertiserSearch(currentAccessJstAdvertisers);

    useEffect(() => {
      if (currentAccessSearchTerm !== jstSearchTerm) {
        setJstSearchTerm(currentAccessSearchTerm);
      }
    }, [currentAccessSearchTerm, jstSearchTerm, setJstSearchTerm]);

    const {
      searchTerm: ctvSearchTerm,
      filteredData: filteredCtvAdvertisers,
      updateSearchTerm: setCtvSearchTerm
    } = useAdvertiserSearch(currentAccessCtvAdvertisers);

    useEffect(() => {
      if (currentAccessSearchTerm !== ctvSearchTerm) {
        setCtvSearchTerm(currentAccessSearchTerm);
      }
    }, [currentAccessSearchTerm, ctvSearchTerm, setCtvSearchTerm]);

    const {
      searchTerm: gtvSearchTerm,
      filteredData: filteredGtvAdvertisers,
      updateSearchTerm: setGtvSearchTerm
    } = useAdvertiserSearch(currentAccessGtvAdvertisers);

    useEffect(() => {
      if (currentAccessSearchTerm !== gtvSearchTerm) {
        setGtvSearchTerm(currentAccessSearchTerm);
      }
    }, [currentAccessSearchTerm, gtvSearchTerm, setGtvSearchTerm]);

    useEffect(() => {
      if (currentUserData?.role === 'admin' && fetchAdvertiserMetaDetails) {
        fetchAdvertiserMetaDetails();
      }
    }, [currentUserData?.role, fetchAdvertiserMetaDetails]);

    if (!formData || !setFormData || !errors) {
      return null;
    }

    const handleRemoveUtcAdvertiser = (advertiserName) => {
      if (canModifyAdvertiser(advertiserName, 'UTC')) {
        removeAdvertiser(advertiserName, 'UTC');
      } else {
        console.warn(`Cannot remove advertiser ${advertiserName} - access denied`);
      }
    };

    const handleRemoveJstAdvertiser = (advertiserName) => {
      if (canModifyAdvertiser(advertiserName, 'JST')) {
        removeAdvertiser(advertiserName, 'JST');
      } else {
        console.warn(`Cannot remove advertiser ${advertiserName} - access denied`);
      }
    };

    const handleRemoveCtvAdvertiser = (advertiserName) => {
      if (canModifyAdvertiser(advertiserName, 'UTC')) {
        removeAdvertiser(advertiserName, 'UTC', 'CTV');
      } else {
        console.warn(`Cannot remove CTV advertiser ${advertiserName} - access denied`);
      }
    };

    const handleRemoveGtvAdvertiser = (advertiserName) => {
      if (canModifyAdvertiser(advertiserName, 'UTC')) {
        removeAdvertiser(advertiserName, 'UTC', 'GTV');
      } else {
        console.warn(`Cannot remove GTV advertiser ${advertiserName} - access denied`);
      }
    };

    const handleAdvertiserAdded = (advertiser, timezone, platform = 'DSP') => {
      addAdvertiser(advertiser, timezone, platform);
    };

    return (
      <>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
            Admin Advertiser Access Management
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage advertiser access for invited users
          </Typography>
        </Box>

        {/* User & Organization Management Panel */}
        <Box
          sx={{
            p: 3,
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            mb: 3
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
            User & Organization Management
          </Typography>

          {/* Search User Section - Full Field */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
              Search User
            </Typography>

            {/* User Search Bar - Full Width */}
            <TextField
              fullWidth
              placeholder="Search by email address..."
              variant="outlined"
              size="medium"
              value={userSearchTerm || ''}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: 2
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      color: 'primary.main',
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    🔍
                  </Box>
                )
              }}
            />

            {/* Search Results */}
            {userSearchTerm.trim() && filteredUsers.length > 0 && (
              <Box sx={{ mb: 2, maxHeight: '200px', overflow: 'auto' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                  Search Results ({filteredUsers.length} users found):
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {filteredUsers.slice(0, 5).map((user, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          email: user.email_id || user.email || '',
                          // Auto-fill organization from get_all_users_list
                          org: user.org_utc && user.org_utc.length > 0 ? user.org_utc.join(', ') : '',
                          // Auto-fill admin checkbox
                          isAdmin: user.role === 'admin',
                          // Auto-fill existing advertisers
                          advertiserAccess: (() => {
                            const allAdvertisers = [...(user.advertisers_utc || []), ...(user.advertisers_non_utc || [])];
                            if (allAdvertisers.length > 0) {
                              const advertiserNames = allAdvertisers.map((adv) => adv.advertiser_name || adv.name);
                              return advertiserNames.join(', ');
                            }
                            return '';
                          })()
                        }));

                        // Don't auto-populate selectedOrgs with user's existing organizations
                        // selectedOrgs should only contain newly selected organizations from dropdown
                        setSelectedOrgs([]);

                        // Also trigger the fetch to populate the Current Access sections
                        if (user.email_id || user.email) {
                          fetchUserAdvertiserDetails(user.email_id || user.email);
                        }

                        setUserSearchTerm(''); // Clear search after selection
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.email_id || user.email || 'No email'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Email, Fetch Button, and Organization Management Row */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 3 }}>
            {/* Email Input Field */}
            <TextField
              name="email"
              label="Email"
              type="email"
              placeholder="user@inmobi.com"
              value={formData.email || ''}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, email: e.target.value }));
              }}
              error={!!errors.email}
              helperText={
                errors.email || (formData.email && formData.email.includes('@inmobi.com') ? '' : 'Enter a valid InMobi email address')
              }
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  height: '56px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            {/* Fetch Button */}
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                if (formData.email && formData.email.includes('@inmobi.com')) {
                  fetchUserAdvertiserDetails(formData.email);
                }
              }}
              disabled={!formData.email || !formData.email.includes('@inmobi.com')}
              sx={{
                height: '56px',
                minWidth: '120px',
                borderRadius: '24px',
                backgroundColor: '#fff',
                color: '#3c4043',
                border: '1px solid #dadce0',
                boxShadow: 'rgba(0, 0, 0, .2) 0 3px 5px -1px, rgba(0, 0, 0, .14) 0 6px 10px 0, rgba(0, 0, 0, .12) 0 1px 18px 0',
                fontFamily: '"Google Sans", Roboto, Arial, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: '.25px',
                textTransform: 'none',
                transition:
                  'box-shadow 280ms cubic-bezier(.4, 0, .2, 1), opacity 15ms linear 30ms, transform 270ms cubic-bezier(0, 0, .2, 1) 0ms',
                '&:hover': {
                  background: '#F6F9FE',
                  color: '#174ea6',
                  boxShadow: 'rgba(60, 64, 67, .3) 0 2px 3px 0, rgba(60, 64, 67, .15) 0 6px 10px 4px'
                },
                '&:disabled': {
                  opacity: 0.6,
                  cursor: 'not-allowed',
                  backgroundColor: '#f1f3f4',
                  color: '#5f6368'
                }
              }}
            >
              {isAdvertiserLoading ? <CircularProgress size={20} color="inherit" /> : 'Fetch'}
            </Button>

            {/* Organization Management Section */}
            <Box
              sx={{
                flex: 1,
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                Organization Management
              </Typography>

              {/* Organization Dropdown */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Organizations</InputLabel>
                <Select
                  multiple
                  value={selectedOrgs || []}
                  onChange={(e) => {
                    setSelectedOrgs(e.target.value);
                    setIsFormModified(true);
                  }}
                  input={<OutlinedInput label="Select Organizations" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((org) => (
                        <Chip key={org} label={org} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {(() => {
                    let availableOrgs = [];
                    if (currentUserData?.role === 'admin') {
                      const allOrgs = new Set();
                      if (advertiserMetaDetails?.advertisers_utc) {
                        advertiserMetaDetails.advertisers_utc.forEach((adv) => {
                          if (adv.org_name) allOrgs.add(adv.org_name);
                        });
                      }

                      if (advertiserMetaDetails?.advertisers_non_utc) {
                        advertiserMetaDetails.advertisers_non_utc.forEach((adv) => {
                          if (adv.org_name) allOrgs.add(adv.org_name);
                        });
                      }
                      availableOrgs = Array.from(allOrgs);
                    } else {
                      availableOrgs = currentUserData?.org_utc || [];
                    }
                    const allExistingOrgs = [];
                    const user = allUsersList?.find((u) => u.email_id === formData.email);
                    if (user && user.org_utc && user.org_utc.length > 0) {
                      allExistingOrgs.push(...user.org_utc);
                    }
                    // Use editableInvitedUserData for organization filtering (simple approach)
                    if (editableInvitedUserData?.advertisers_utc) {
                      editableInvitedUserData.advertisers_utc.forEach((adv) => {
                        if (adv.org_name && !allExistingOrgs.includes(adv.org_name)) {
                          allExistingOrgs.push(adv.org_name);
                        }
                      });
                    }
                    if (editableInvitedUserData?.advertisers_non_utc) {
                      editableInvitedUserData.advertisers_non_utc.forEach((adv) => {
                        if (adv.org_name && !allExistingOrgs.includes(adv.org_name)) {
                          allExistingOrgs.push(adv.org_name);
                        }
                      });
                    }
                    const allDisplayedOrgs = [...new Set([...allExistingOrgs, ...(selectedOrgs || [])])];
                    const filteredOrgs = availableOrgs.filter((org) => !allDisplayedOrgs.includes(org));
                    return filteredOrgs.map((org) => (
                      <MenuItem key={org} value={org}>
                        {org}
                      </MenuItem>
                    ));
                  })()}
                </Select>
              </FormControl>

              {/* Access Control Message for Internal Users - Under Organization Dropdown */}
              {currentUserData?.role === 'internal' && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'info.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'info.200'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'info.700' }}>
                    Access Control Information:
                  </Typography>
                  <Typography variant="caption" color="info.700" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    🟢 Green dot: Can be managed by you (from your scope)
                  </Typography>
                  <Typography variant="caption" color="info.700" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    🔒 Lock icon: Cannot be modified (outside your scope)
                  </Typography>
                  <Typography variant="caption" color="info.600" sx={{ ml: 2 }}>
                    Grey chips: Cannot be modified (outside your scope)
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Admin Checkbox - Under Email Field */}
          {currentUserData?.role === 'admin' && (
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={<Checkbox checked={formData.isAdmin || false} onChange={handleChange} name="isAdmin" color="primary" />}
                label={
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Admin User
                  </Typography>
                }
              />
            </Box>
          )}

          {/* User's Organizations - Under Organization Management */}
          {(() => {
            const existingOrgs = [];
            const user = allUsersList?.find((u) => u.email_id === formData.email);
            if (user && user.org_utc && user.org_utc.length > 0) {
              existingOrgs.push(...user.org_utc);
            }

            // Filter out removed organizations from existing organizations
            const filteredExistingOrgs = existingOrgs.filter((org) => !removedOrgs.includes(org));

            const allOrgs = [...new Set([...filteredExistingOrgs, ...(selectedOrgs || [])])];

            return allOrgs.length > 0 ? (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                  User&apos;s Organizations
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {allOrgs.map((org) => {
                    const isExisting = filteredExistingOrgs.includes(org);
                    const isNewlySelected = selectedOrgs?.includes(org);
                    const canModify = canModifyOrganization(org);

                    return (
                      <Chip
                        key={org}
                        label={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            {canModify ? (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: '#4caf50'
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: '#9e9e9e'
                                }}
                              />
                            )}
                            {org}
                            {!canModify && <Box sx={{ ml: 0.5, fontSize: '12px' }}>🔒</Box>}
                          </Box>
                        }
                        onDelete={
                          canModify
                            ? () => {
                                if (isNewlySelected) {
                                  // Remove from newly selected organizations
                                  setSelectedOrgs(selectedOrgs.filter((o) => o !== org));
                                  setIsFormModified(true);
                                } else if (isExisting) {
                                  // For existing organizations, we need to remove all associated advertisers
                                  // and then the organization will be removed from the UI

                                  // Get advertisers from get_all_users_list data
                                  const user = allUsersList?.find((u) => u.email_id === formData.email);
                                  const utcAdvertisersToRemove = user?.advertisers_utc?.filter((adv) => adv.org_name === org) || [];
                                  const jstAdvertisersToRemove = user?.advertisers_non_utc?.filter((adv) => adv.org_name === org) || [];
                                  const ctvAdvertisersToRemove = user?.advertisers_ctv?.filter((adv) => adv.org_name === org) || [];
                                  const gtvAdvertisersToRemove = user?.advertisers_gtv?.filter((adv) => adv.org_name === org) || [];

                                  // Remove each advertiser using the existing removeAdvertiser function
                                  utcAdvertisersToRemove.forEach((advertiser) => {
                                    removeAdvertiser(advertiser.name, 'UTC');
                                  });
                                  jstAdvertisersToRemove.forEach((advertiser) => {
                                    removeAdvertiser(advertiser.name, 'JST');
                                  });
                                  ctvAdvertisersToRemove.forEach((advertiser) => {
                                    removeAdvertiser(advertiser.name, 'UTC', 'CTV');
                                  });
                                  gtvAdvertisersToRemove.forEach((advertiser) => {
                                    removeAdvertiser(advertiser.name, 'UTC', 'GTV');
                                  });

                                  // If no advertisers were found, we need to remove the organization directly
                                  if (
                                    utcAdvertisersToRemove.length === 0 &&
                                    jstAdvertisersToRemove.length === 0 &&
                                    ctvAdvertisersToRemove.length === 0 &&
                                    gtvAdvertisersToRemove.length === 0
                                  ) {
                                    const newRemovedOrgs = [...removedOrgs, org];
                                    // Notify parent component about removed organizations
                                    if (onRemovedOrgsChange) {
                                      onRemovedOrgsChange(newRemovedOrgs);
                                    }
                                  }

                                  setIsFormModified(true);
                                }
                              }
                            : undefined
                        }
                        color={isExisting ? 'default' : 'primary'}
                        variant={isExisting ? 'filled' : 'outlined'}
                        sx={{
                          opacity: canModify ? 1 : 0.6,
                          cursor: canModify ? 'pointer' : 'not-allowed',
                          bgcolor: canModify ? undefined : '#f5f5f5'
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            ) : null;
          })()}
        </Box>

        {/* Panel 2: Current Access - Existing Advertisers */}
        <Box
          sx={{
            p: 3,
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            mb: 3
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
            Current Access - Existing Advertisers
          </Typography>

          <Box sx={{ mb: 2, maxWidth: 280 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="current-access-platform-label">View by platform</InputLabel>
              <Select
                labelId="current-access-platform-label"
                label="View by platform"
                value={currentAccessPlatform}
                onChange={(e) => setCurrentAccessPlatform(e.target.value)}
              >
                <MenuItem value="DSP">DSP (UTC / JST)</MenuItem>
                <MenuItem value="CTV">CTV (UTC only)</MenuItem>
                <MenuItem value="GTV">GTV (UTC only)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Search Bar for Current Access */}
          <TextField
            placeholder="Search current advertisers..."
            variant="outlined"
            size="small"
            value={currentAccessSearchTerm || ''}
            onChange={(e) => setCurrentAccessSearchTerm(e.target.value)}
            sx={{
              width: '300px',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'white',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: 2
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <Box
                  sx={{
                    color: 'primary.main',
                    mr: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  🔍
                </Box>
              )
            }}
          />

          {/* DSP: UTC + JST panels */}
          {currentAccessPlatform === 'DSP' && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'row',
              alignItems: 'flex-start',
              flexWrap: 'nowrap',
              overflow: 'hidden',
              mt: 3
            }}
          >
            {/* DSP — UTC */}
            <Paper
              elevation={2}
              sx={{
                flex: 1,
                p: 2,
                border: '2px dashed #1976d2',
                backgroundColor: '#f3f6ff',
                height: '200px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  DSP — UTC ({filteredUtcAdvertisers.length})
                </Typography>
                {filteredUtcAdvertisers.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      // Track which organizations will be affected
                      const affectedOrgs = new Set();
                      const advertisersToRemove = [];

                      filteredUtcAdvertisers.forEach((advertiser) => {
                        if (advertiser.org_name) {
                          affectedOrgs.add(advertiser.org_name);
                        }
                        advertisersToRemove.push(advertiser.name);
                      });

                      // Remove all advertisers first
                      advertisersToRemove.forEach((advertiserName) => {
                        removeAdvertiser(advertiserName, 'UTC');
                      });

                      // Calculate remaining advertisers manually for each affected organization
                      affectedOrgs.forEach((orgName) => {
                        // Calculate remaining UTC advertisers (current - removed)
                        const remainingUtcAdvertisers = currentAccessUtcAdvertisers.filter(
                          (adv) => adv.org_name === orgName && !advertisersToRemove.includes(adv.name)
                        );

                        // Check JST + CTV + GTV advertisers (unchanged)
                        const hasRemainingJstAdvertisers = currentAccessJstAdvertisers.some((adv) => adv.org_name === orgName);
                        const hasRemainingCtvAdvertisers = currentAccessCtvAdvertisers.some((adv) => adv.org_name === orgName);
                        const hasRemainingGtvAdvertisers = currentAccessGtvAdvertisers.some((adv) => adv.org_name === orgName);

                        console.log(`🔍 Checking org ${orgName}:`, {
                          advertisersToRemove,
                          remainingUtcAdvertisers: remainingUtcAdvertisers.length,
                          hasRemainingJstAdvertisers,
                          hasRemainingCtvAdvertisers,
                          hasRemainingGtvAdvertisers,
                          shouldRemove:
                            remainingUtcAdvertisers.length === 0 &&
                            !hasRemainingJstAdvertisers &&
                            !hasRemainingCtvAdvertisers &&
                            !hasRemainingGtvAdvertisers
                        });

                        if (
                          remainingUtcAdvertisers.length === 0 &&
                          !hasRemainingJstAdvertisers &&
                          !hasRemainingCtvAdvertisers &&
                          !hasRemainingGtvAdvertisers
                        ) {
                          console.log(`✅ Removing organization: ${orgName}`);
                          // Add organization to removed list
                          const newRemovedOrgs = [...removedOrgs, orgName];
                          if (onRemovedOrgsChange) {
                            onRemovedOrgsChange(newRemovedOrgs);
                          }
                        }
                      });

                      setIsFormModified(true);
                    }}
                    sx={{ fontSize: '10px', py: 0.25, px: 1 }}
                  >
                    Remove All
                  </Button>
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  alignItems: 'flex-start',
                  p: 0.5
                }}
              >
                {filteredUtcAdvertisers.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    {currentAccessSearchTerm ? 'No DSP UTC advertisers matching search' : 'No DSP UTC advertisers'}
                  </Typography>
                ) : (
                  filteredUtcAdvertisers.map((advertiser, index) => {
                    const canModify = canModifyAdvertiser(advertiser.name, 'UTC');

                    return (
                      <Chip
                        key={`utc-existing-${advertiser.id || advertiser.name}-${index}`}
                        label={advertiser.name}
                        variant="filled"
                        size="small"
                        onDelete={canModify ? () => handleRemoveUtcAdvertiser(advertiser.name) : undefined}
                        sx={{
                          backgroundColor: canModify ? '#1976d2' : '#9e9e9e',
                          color: 'white',
                          fontWeight: 'normal',
                          fontSize: '11px',
                          height: '24px',
                          opacity: canModify ? 1 : 0.7,
                          cursor: canModify ? 'pointer' : 'not-allowed',
                          '& .MuiChip-label': { fontSize: '11px', px: 1 },
                          '& .MuiChip-deleteIcon': {
                            color: 'white',
                            fontSize: '16px',
                            display: canModify ? 'flex' : 'none'
                          },
                          '&:hover': {
                            backgroundColor: canModify ? '#1565c0' : '#9e9e9e'
                          }
                        }}
                      />
                    );
                  })
                )}
              </Box>
            </Paper>

            {/* DSP — JST */}
            <Paper
              elevation={2}
              sx={{
                flex: 1,
                p: 2,
                border: '2px dashed #ff6b35',
                backgroundColor: '#fff4f0',
                height: '200px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#ff6b35', fontWeight: 'bold' }}>
                  DSP — JST ({filteredJstAdvertisers.length})
                </Typography>
                {filteredJstAdvertisers.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      // Track which organizations will be affected
                      const affectedOrgs = new Set();
                      const advertisersToRemove = [];

                      filteredJstAdvertisers.forEach((advertiser) => {
                        if (advertiser.org_name) {
                          affectedOrgs.add(advertiser.org_name);
                        }
                        advertisersToRemove.push(advertiser.name);
                      });

                      // Remove all advertisers first
                      advertisersToRemove.forEach((advertiserName) => {
                        removeAdvertiser(advertiserName, 'JST');
                      });

                      // Calculate remaining advertisers manually for each affected organization
                      affectedOrgs.forEach((orgName) => {
                        // Check UTC + CTV + GTV advertisers (unchanged)
                        const hasRemainingUtcAdvertisers = currentAccessUtcAdvertisers.some((adv) => adv.org_name === orgName);
                        const hasRemainingCtvAdvertisers = currentAccessCtvAdvertisers.some((adv) => adv.org_name === orgName);
                        const hasRemainingGtvAdvertisers = currentAccessGtvAdvertisers.some((adv) => adv.org_name === orgName);

                        // Calculate remaining JST advertisers (current - removed)
                        const remainingJstAdvertisers = currentAccessJstAdvertisers.filter(
                          (adv) => adv.org_name === orgName && !advertisersToRemove.includes(adv.name)
                        );

                        console.log(`🔍 Checking org ${orgName}:`, {
                          advertisersToRemove,
                          hasRemainingUtcAdvertisers,
                          hasRemainingCtvAdvertisers,
                          hasRemainingGtvAdvertisers,
                          remainingJstAdvertisers: remainingJstAdvertisers.length,
                          shouldRemove:
                            !hasRemainingUtcAdvertisers &&
                            remainingJstAdvertisers.length === 0 &&
                            !hasRemainingCtvAdvertisers &&
                            !hasRemainingGtvAdvertisers
                        });

                        if (
                          !hasRemainingUtcAdvertisers &&
                          remainingJstAdvertisers.length === 0 &&
                          !hasRemainingCtvAdvertisers &&
                          !hasRemainingGtvAdvertisers
                        ) {
                          console.log(`✅ Removing organization: ${orgName}`);
                          // Add organization to removed list
                          const newRemovedOrgs = [...removedOrgs, orgName];
                          if (onRemovedOrgsChange) {
                            onRemovedOrgsChange(newRemovedOrgs);
                          }
                        }
                      });

                      setIsFormModified(true);
                    }}
                    sx={{ fontSize: '10px', py: 0.25, px: 1 }}
                  >
                    Remove All
                  </Button>
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  alignItems: 'flex-start',
                  p: 0.5
                }}
              >
                {filteredJstAdvertisers.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    {currentAccessSearchTerm ? 'No DSP JST advertisers matching search' : 'No DSP JST advertisers'}
                  </Typography>
                ) : (
                  filteredJstAdvertisers.map((advertiser, index) => {
                    const canModify = canModifyAdvertiser(advertiser.name, 'JST');

                    return (
                      <Chip
                        key={`jst-existing-${advertiser.id || advertiser.name}-${index}`}
                        label={advertiser.name}
                        variant="filled"
                        size="small"
                        onDelete={canModify ? () => handleRemoveJstAdvertiser(advertiser.name) : undefined}
                        sx={{
                          backgroundColor: canModify ? '#ff6b35' : '#9e9e9e',
                          color: 'white',
                          fontWeight: 'normal',
                          fontSize: '11px',
                          height: '24px',
                          opacity: canModify ? 1 : 0.7,
                          cursor: canModify ? 'pointer' : 'not-allowed',
                          '& .MuiChip-label': { fontSize: '11px', px: 1 },
                          '& .MuiChip-deleteIcon': {
                            color: 'white',
                            fontSize: '16px',
                            display: canModify ? 'flex' : 'none'
                          },
                          '&:hover': {
                            backgroundColor: canModify ? '#e55a2b' : '#9e9e9e'
                          }
                        }}
                      />
                    );
                  })
                )}
              </Box>
            </Paper>
          </Box>
          )}

          {currentAccessPlatform === 'CTV' && (
            <Box sx={{ mt: 3 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  border: '2px dashed #7b1fa2',
                  backgroundColor: '#f3e5f5',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#7b1fa2', fontWeight: 'bold' }}>
                    CTV — UTC ({filteredCtvAdvertisers.length})
                  </Typography>
                  {filteredCtvAdvertisers.length > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        const affectedOrgs = new Set();
                        const advertisersToRemove = [];
                        filteredCtvAdvertisers.forEach((advertiser) => {
                          if (advertiser.org_name) affectedOrgs.add(advertiser.org_name);
                          advertisersToRemove.push(advertiser.name);
                        });
                        advertisersToRemove.forEach((advertiserName) => {
                          removeAdvertiser(advertiserName, 'UTC', 'CTV');
                        });
                        affectedOrgs.forEach((orgName) => {
                          const remainingCtv = currentAccessCtvAdvertisers.filter(
                            (adv) => adv.org_name === orgName && !advertisersToRemove.includes(adv.name)
                          );
                          const hasRemainingUtc = currentAccessUtcAdvertisers.some((adv) => adv.org_name === orgName);
                          const hasRemainingJst = currentAccessJstAdvertisers.some((adv) => adv.org_name === orgName);
                          const hasRemainingGtv = currentAccessGtvAdvertisers.some((adv) => adv.org_name === orgName);
                          if (remainingCtv.length === 0 && !hasRemainingUtc && !hasRemainingJst && !hasRemainingGtv) {
                            const newRemovedOrgs = [...removedOrgs, orgName];
                            if (onRemovedOrgsChange) {
                              onRemovedOrgsChange(newRemovedOrgs);
                            }
                          }
                        });
                        setIsFormModified(true);
                      }}
                      sx={{ fontSize: '10px', py: 0.25, px: 1 }}
                    >
                      Remove All
                    </Button>
                  )}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    alignItems: 'flex-start',
                    p: 0.5
                  }}
                >
                  {filteredCtvAdvertisers.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                      {currentAccessSearchTerm ? 'No CTV advertisers matching search' : 'No CTV advertisers'}
                    </Typography>
                  ) : (
                    filteredCtvAdvertisers.map((advertiser, index) => {
                      const canModify = canModifyAdvertiser(advertiser.name, 'UTC');
                      return (
                        <Chip
                          key={`ctv-existing-${advertiser.id || advertiser.name}-${index}`}
                          label={advertiser.name}
                          variant="filled"
                          size="small"
                          onDelete={canModify ? () => handleRemoveCtvAdvertiser(advertiser.name) : undefined}
                          sx={{
                            backgroundColor: canModify ? '#7b1fa2' : '#9e9e9e',
                            color: 'white',
                            fontWeight: 'normal',
                            fontSize: '11px',
                            height: '24px',
                            opacity: canModify ? 1 : 0.7,
                            cursor: canModify ? 'pointer' : 'not-allowed',
                            '& .MuiChip-label': { fontSize: '11px', px: 1 },
                            '& .MuiChip-deleteIcon': {
                              color: 'white',
                              fontSize: '16px',
                              display: canModify ? 'flex' : 'none'
                            },
                            '&:hover': {
                              backgroundColor: canModify ? '#6a1b9a' : '#9e9e9e'
                            }
                          }}
                        />
                      );
                    })
                  )}
                </Box>
              </Paper>
            </Box>
          )}

          {currentAccessPlatform === 'GTV' && (
            <Box sx={{ mt: 3 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  border: '2px dashed #00796b',
                  backgroundColor: '#e0f2f1',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#00796b', fontWeight: 'bold' }}>
                    GTV — UTC ({filteredGtvAdvertisers.length})
                  </Typography>
                  {filteredGtvAdvertisers.length > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        const affectedOrgs = new Set();
                        const advertisersToRemove = [];
                        filteredGtvAdvertisers.forEach((advertiser) => {
                          if (advertiser.org_name) affectedOrgs.add(advertiser.org_name);
                          advertisersToRemove.push(advertiser.name);
                        });
                        advertisersToRemove.forEach((advertiserName) => {
                          removeAdvertiser(advertiserName, 'UTC', 'GTV');
                        });
                        affectedOrgs.forEach((orgName) => {
                          const remainingGtv = currentAccessGtvAdvertisers.filter(
                            (adv) => adv.org_name === orgName && !advertisersToRemove.includes(adv.name)
                          );
                          const hasRemainingUtc = currentAccessUtcAdvertisers.some((adv) => adv.org_name === orgName);
                          const hasRemainingJst = currentAccessJstAdvertisers.some((adv) => adv.org_name === orgName);
                          const hasRemainingCtv = currentAccessCtvAdvertisers.some((adv) => adv.org_name === orgName);
                          if (remainingGtv.length === 0 && !hasRemainingUtc && !hasRemainingJst && !hasRemainingCtv) {
                            const newRemovedOrgs = [...removedOrgs, orgName];
                            if (onRemovedOrgsChange) {
                              onRemovedOrgsChange(newRemovedOrgs);
                            }
                          }
                        });
                        setIsFormModified(true);
                      }}
                      sx={{ fontSize: '10px', py: 0.25, px: 1 }}
                    >
                      Remove All
                    </Button>
                  )}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    alignItems: 'flex-start',
                    p: 0.5
                  }}
                >
                  {filteredGtvAdvertisers.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                      {currentAccessSearchTerm ? 'No GTV advertisers matching search' : 'No GTV advertisers'}
                    </Typography>
                  ) : (
                    filteredGtvAdvertisers.map((advertiser, index) => {
                      const canModify = canModifyAdvertiser(advertiser.name, 'UTC');
                      return (
                        <Chip
                          key={`gtv-existing-${advertiser.id || advertiser.name}-${index}`}
                          label={advertiser.name}
                          variant="filled"
                          size="small"
                          onDelete={canModify ? () => handleRemoveGtvAdvertiser(advertiser.name) : undefined}
                          sx={{
                            backgroundColor: canModify ? '#00796b' : '#9e9e9e',
                            color: 'white',
                            fontWeight: 'normal',
                            fontSize: '11px',
                            height: '24px',
                            opacity: canModify ? 1 : 0.7,
                            cursor: canModify ? 'pointer' : 'not-allowed',
                            '& .MuiChip-label': { fontSize: '11px', px: 1 },
                            '& .MuiChip-deleteIcon': {
                              color: 'white',
                              fontSize: '16px',
                              display: canModify ? 'flex' : 'none'
                            },
                            '&:hover': {
                              backgroundColor: canModify ? '#00695c' : '#9e9e9e'
                            }
                          }}
                        />
                      );
                    })
                  )}
                </Box>
              </Paper>
            </Box>
          )}
        </Box>

        {/* Panel 3: Available Advertisers - Select timezone when adding */}
        <AvailableAdvertisersSection
          user_role={currentUserData?.role || 'admin'}
          currentUserAdvertisers={currentUserData?.role === 'admin' ? advertiserMetaDetails : currentUserAdvertisers}
          invitedUserExistingAccess={editableInvitedUserData}
          onAdvertiserAdded={handleAdvertiserAdded}
        />

        {/* Error Display */}
        {(advertiserError || currentUserError) && (
          <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 2, mb: 2 }}>
            <Typography variant="body2" color="error.dark">
              {advertiserError || currentUserError}
            </Typography>
          </Box>
        )}
      </>
    );
  }
);

AdminAdvertiserAccess.propTypes = {
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  allUsersList: PropTypes.array.isRequired,
  selectedOrgs: PropTypes.array,
  setSelectedOrgs: PropTypes.func,
  removedOrgs: PropTypes.array,
  advertiserMetaDetails: PropTypes.object,
  currentUserData: PropTypes.object,
  fetchAdvertisersListForUserBasedOnEmailId: PropTypes.func,
  fetchAdvertiserMetaDetails: PropTypes.func,
  onCurrentAccessDataChange: PropTypes.func,
  handleChange: PropTypes.func,
  setIsFormModified: PropTypes.func,
  onRemovedOrgsChange: PropTypes.func
};

export default AdminAdvertiserAccess;
